/**
 * Route Registry — R6 (EXECUTION_PLAN §0)
 *
 * Each feature registers its own routes here instead of editing App.js directly.
 * This file is the single point of truth for what routes exist in the app.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * HOW TO REGISTER A FEATURE'S ROUTES
 * ────────────────────────────────────────────────────────────────────────────
 *
 * 1. Create `src/features/<name>/routes.js` exporting an array of route objects:
 *
 *    // src/features/auth/routes.js
 *    import React from 'react';
 *    import { Login } from './pages/Login';
 *    import { Register } from './pages/Register';
 *
 *    const authRoutes = [
 *      // public routes (no auth required)
 *      { path: '/:slug/login',    element: <Login />,    public: true },
 *      { path: '/:slug/registro', element: <Register />, public: true },
 *
 *      // protected route — RequireRole checks role from AuthProvider
 *      { path: '/:slug/app/dashboard', element: <Dashboard />, roles: ['OWNER', 'TEACHER', 'STUDENT'] },
 *    ];
 *
 *    export default authRoutes;
 *
 * 2. Import and register in this file:
 *
 *    import authRoutes from '../../features/auth/routes';
 *    registry.register(authRoutes);
 *
 * 3. Done. AppRouter reads registry.routes and renders them.
 *    NOBODY edits App.js routes directly after this point.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * ROUTE OBJECT SHAPE
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  {
 *    path:    string                   — react-router path pattern
 *    element: React.ReactElement       — component to render
 *    public?: boolean                  — if true, no auth check (default: false)
 *    roles?:  string[]                 — allowed roles; omit = any authenticated user
 *    children?: RouteObject[]          — nested routes (optional)
 *  }
 */

const _routes = [];

const registry = {
  /**
   * Register an array of route objects from a feature module.
   * @param {RouteObject[]} routes
   */
  register(routes) {
    _routes.push(...routes);
  },

  /** All registered routes (read-only view). AppRouter reads this. */
  get routes() {
    return _routes;
  },
};

export { registry };
